import axios from 'axios';
import { environment } from '../environments/environment';
import { isTokenValid, clearTokens } from '../utils/authUtils';
import logger from '../utils/logger';

// ==========================
// Axios Instances
// ==========================
export const apiClient = axios.create({
  baseURL: environment.apiBaseUrl,
  timeout: environment.timeout,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  }
});

// ==========================
// Token Refresh State
// ==========================
let isRefreshing = false;
let refreshCallback = null;

export const setAuthRefreshCallback = (callback) => {
  refreshCallback = callback;
};

// ==========================
// Loader integration
// ==========================
let loadingBar = null;

export const setLoadingBar = (bar) => {
  loadingBar = bar; // bar must have { start, complete }
};

// ==========================
// Wait for auth token with timeout
// ==========================
const waitForToken = (timeoutMs = 5000) => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const checkToken = () => {
      const authToken = localStorage.getItem('AuthToken');
      if (authToken) {
        resolve(authToken);
      } else if (Date.now() - startTime > timeoutMs) {
        reject(new Error('Token wait timeout'));
      } else {
        setTimeout(checkToken, 100);
      }
    };
    checkToken();
  });
};

// ==========================
// Token Refresh Helper
// ==========================
const refreshTokens = async () => {
  if (!refreshCallback) {
    logger.warn('No refresh callback configured');
    return false;
  }

  if (isRefreshing) {
    // Wait for ongoing refresh
    while (isRefreshing) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    // Verify tokens after waiting
    const authToken = localStorage.getItem('AuthToken');
    const accessToken = localStorage.getItem('AccessToken');
    const externalToken = localStorage.getItem('ExternalAuthToken');
    return isTokenValid(authToken) && isTokenValid(accessToken) && isTokenValid(externalToken);
  }

  isRefreshing = true;
  try {
    logger.log('Refreshing tokens...');
    await refreshCallback();

    // Verify all three tokens are present and valid after refresh
    const authToken = localStorage.getItem('AuthToken');
    const accessToken = localStorage.getItem('AccessToken');
    const externalToken = localStorage.getItem('ExternalAuthToken');

    if (!isTokenValid(authToken) || !isTokenValid(accessToken) || !isTokenValid(externalToken)) {
      logger.error('Token refresh incomplete - not all tokens are valid');
      return false;
    }

    logger.log('Tokens refreshed successfully');
    return true;
  } catch (error) {
    logger.error('Failed to refresh tokens:', error);
    return false;
  } finally {
    isRefreshing = false;
  }
};

// ==========================
// Request Interceptors
// ==========================
const setupRequestInterceptors = (client) => {
  const skipTokenEndpoints = ["/account/access-token", "/account/login"];

  client.interceptors.request.use(
    async (config) => {
      const shouldSkipAuthToken = skipTokenEndpoints.some(
        endpoint => config.url === endpoint
      );

      const shouldSkipExternalAuthToken = shouldSkipAuthToken || config.url === '/account/login-external';

      let authToken = null;
      let externalToken = null;
      let accessToken = null;

      if (!shouldSkipAuthToken) {
        authToken = localStorage.getItem('AuthToken');
        accessToken = localStorage.getItem('AccessToken');
        externalToken = localStorage.getItem('ExternalAuthToken');

        // Check if tokens need refresh
        const needsRefresh = !isTokenValid(authToken) || !isTokenValid(accessToken) || (!shouldSkipExternalAuthToken && !isTokenValid(externalToken));

        if (needsRefresh) {
          logger.warn('Tokens invalid or expired, attempting refresh');

          // Only clear tokens if refresh is not already in progress
          if (!isRefreshing) {
            clearTokens();
          }

          const refreshed = await refreshTokens();
          if (!refreshed && !authToken) {
            // First-time initialization - wait for token
            try {
              await waitForToken();
            } catch (error) {
              logger.error('Token initialization timeout:', error);
              throw error;
            }
          }

          // Re-fetch tokens after refresh
          authToken = localStorage.getItem('AuthToken');
          accessToken = localStorage.getItem('AccessToken');
          externalToken = localStorage.getItem('ExternalAuthToken');
        }
      }

      if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }

      if (externalToken) {
        config.headers.ExternalAuthorization = externalToken;
      }

      const aadAssertion = localStorage.getItem('AccessToken');
      if (aadAssertion) {
        config.headers.AadAssertion = aadAssertion;
      }

      // Check if silent mode is enabled (skip loading bar)
      if (loadingBar && !config.headers['X-Silent-Request']) loadingBar.start();
      return config;
    },
    (error) => {
      if (loadingBar) loadingBar.complete();
      return Promise.reject(error);
    }
  );
};

// ==========================
// Response Interceptors
// ==========================
const setupResponseInterceptors = (client) => {
  client.interceptors.response.use(
    (response) => {
      if (loadingBar && !response.config.headers['X-Silent-Request']) {
        setTimeout(() => loadingBar.complete(), 300);
      }
      return response;
    },
    async (error) => {
      if (loadingBar && !error.config?.headers?.['X-Silent-Request']) {
        setTimeout(() => loadingBar.complete(), 300);
      }

      const originalRequest = error.config;

      // Handle 401 Unauthorized - Token expired or invalid
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        logger.warn('Received 401 - Attempting token refresh and retry');

        // Only clear tokens if refresh is not already in progress
        if (!isRefreshing) {
          clearTokens();
        }

        const refreshed = await refreshTokens();
        if (refreshed) {
          return client(originalRequest);
        }
      }

      // Format error response
      if (error.response) {
        const { status, data } = error.response;
        let message = data?.message || data;

        const statusMessages = {
          401: "Unauthorized - Please login again.",
          403: "Forbidden - You don't have permission.",
          404: "Resource not found.",
          500: "Internal server error."
        };

        return Promise.reject({
          status,
          message: message || statusMessages[status] || "Something went wrong.",
          data,
          isNetworkError: false
        });
      }

      if (error.request) {
        return Promise.reject({
          status: 0,
          message: "Network error - Please check your connection",
          isNetworkError: true
        });
      }

      return Promise.reject({
        status: -1,
        message: error.message || "An unexpected error occurred",
        isNetworkError: false
      });
    }
  );
};

// ==========================
// Apply Interceptors
// ==========================
setupRequestInterceptors(apiClient);
setupResponseInterceptors(apiClient);

// ==========================
// Retry Utility
// ==========================
export const retryRequest = async (requestFn, maxRetries = 1, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      if (i === maxRetries - 1 || !error.isNetworkError) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
};

const httpClient = { apiClient, retryRequest };
export default httpClient;